import React, { useState, useEffect /*useMemo*/ } from "react";
import { BaseModal } from "../../../../atoms/base-modal";
import {
  ClusterWrap,
  FormContainer,
  FormContainerItem,
  PrimaryButton,
  SecondaryButton,
  Select,
  ReactSelect,
  TitleWrap,
  ReactSelectProps,
  DangerSecondaryButton,
} from "@graffer-inc/console-design-kit";
import {
  AssignmentRule,
  Condition,
  Assignment,
  TargetField,
  Principal,
} from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import { MultiGroupSelect } from "../../../../organisms/smart-apply/group-select/multi-group-select";

interface OptionType {
  value: string;
  label: string;
}

interface RuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rule: AssignmentRule) => void;
  initialData?: AssignmentRule;
  targetFields: TargetField[];
}

export const RuleModal: React.FC<RuleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  targetFields,
}) => {
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);

  useEffect(() => {
    if (initialData) {
      setConditions(initialData.conditions);
      setAssignments(initialData.assignments);
    } else {
      setConditions([]);
      setAssignments([]);
    }
  }, [initialData, isOpen]);

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      {
        id: "",
        match: {
          const: "",
        },
      },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
  };

  const handleConditionFieldChange = (index: number, fieldId: string) => {
    const newConditions = [...conditions];
    newConditions[index].id = fieldId;
    newConditions[index].match.const = ""; // フィールドが変わったら値をリセット
    setConditions(newConditions);
  };

  const handleConditionValueChange = (index: number, value: string) => {
    const newConditions = [...conditions];
    newConditions[index].match.const = value;
    setConditions(newConditions);
  };

  const handleMultiGroupSelect = (ids: string[]) => {
    const newAssignments = ids.map((id) => ({
      assignee: {
        type: "groupPrincipal",
        id: id,
      } as Principal,
    }));
    setAssignments(newAssignments);
  };

  const handleSave = () => {
    const isValid = validateRule({ conditions, assignments });

    if (isValid) {
      onSave({
        conditions,
        assignments,
      });
    }
  };

  const validateRule = (rule: AssignmentRule) => {
    return (
      rule.assignments.length > 0 &&
      rule.conditions.every(
        (condition) => condition.id && condition.match.const,
      ) &&
      rule.assignments.every((assignment) => assignment.assignee.id)
    );
  };

  // 各選択項目ごとに選択可能な値のオプションを取得
  const getFieldValueOptions = (fieldId: string) => {
    const field = targetFields.find((f) => f.elemId === fieldId);
    return field
      ? field.expectedValues.map((value) => ({
          value,
          label: value,
        }))
      : [];
  };

  const selectedFieldIds = conditions.flatMap((condition) =>
    condition.id ? [condition.id] : [],
  );

  // 既に選択された項目を除外した選択項目のオプションを取得
  const getAvailableFieldOptions = (currentIndex: number) => {
    const selectedFieldIdsWithoutCurrentIndex = selectedFieldIds.filter(
      (id) => id !== conditions[currentIndex].id,
    );

    return targetFields.flatMap((field) =>
      !selectedFieldIdsWithoutCurrentIndex.includes(field.elemId)
        ? [
            {
              value: field.elemId,
              name: `${field.description}(${field.elemId})`,
            },
          ]
        : [],
    );
  };

  const modalTitle = initialData
    ? "振り分けルールの編集"
    : "振り分けルールの追加";

  return (
    <BaseModal
      isActive={isOpen}
      title={modalTitle}
      onClose={onClose}
      style={{ width: "90%", maxWidth: "1000px" }}
    >
      <section className="modal-card-body">
        <FormContainer>
          <FormContainerItem>
            <TitleWrap
              id="condition-title"
              title="条件設定"
              description="振り分けルールの条件を設定してください。条件が設定されていない場合は無条件で振り分けられます。"
            />
          </FormContainerItem>

          {conditions.map((condition, index) => (
            <FormContainerItem key={`condition-${index}`}>
              <div className="columns">
                <div className="column is-5">
                  <Select
                    label="選択項目"
                    description="条件に使う選択項目を選択してください。"
                    value={condition.id}
                    onChange={(value) =>
                      handleConditionFieldChange(index, value as string)
                    }
                    options={getAvailableFieldOptions(index)}
                    placeholder="選択項目を選択してください"
                  />
                </div>
                <div className="column is-5">
                  <ReactSelect
                    label="値"
                    value={
                      condition.match.const
                        ? {
                            value: condition.match.const,
                            label: condition.match.const,
                          }
                        : undefined
                    }
                    onChange={(selectedOption) => {
                      if (
                        selectedOption &&
                        (selectedOption as OptionType).value
                      ) {
                        handleConditionValueChange(
                          index,
                          (selectedOption as OptionType).value,
                        );
                      }
                    }}
                    options={
                      getFieldValueOptions(
                        condition.id,
                      ) as ReactSelectProps<string>["options"]
                    }
                    placeholder="値を選択してください"
                    disabled={!condition.id}
                    description="条件の値を選択してください"
                  />
                </div>
                <div className="column is-2 is-flex is-align-items-flex-end">
                  <DangerSecondaryButton
                    text="削除"
                    icon="TrashIcon"
                    onClick={() => handleRemoveCondition(index)}
                  />
                </div>
              </div>
            </FormContainerItem>
          ))}

          <FormContainerItem>
            <SecondaryButton
              text="条件追加"
              onClick={handleAddCondition}
              icon="PlusCircleIcon"
              disabled={!(targetFields.length - selectedFieldIds.length > 0)}
            />
          </FormContainerItem>

          <FormContainerItem>
            <TitleWrap id="assignment-title" title="振り分け先設定" required />
          </FormContainerItem>

          <FormContainerItem>
            <div style={{ marginBottom: "64px" }}>
              <MultiGroupSelect
                selectedIds={assignments?.map((a) => a.assignee.id)}
                onSelect={(ids) => {
                  handleMultiGroupSelect(ids);
                }}
                width="100%"
                maxHeight="100px"
              />
            </div>
          </FormContainerItem>
        </FormContainer>
      </section>
      <footer className="modal-card-foot is-justify-content-center">
        <ClusterWrap>
          <SecondaryButton text="キャンセル" onClick={onClose} />
          <PrimaryButton
            text="保存"
            onClick={handleSave}
            disabled={!validateRule({ conditions, assignments })}
          />
        </ClusterWrap>
      </footer>
    </BaseModal>
  );
};
